import { useState, useEffect, useCallback } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Clock, ChevronDown, ChevronUp, Bot, User } from "lucide-react";
import { parseApiDate } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TERMINAISON_LABELS = {
  fin_normale: { label: "Fin normale", className: "bg-green-100 text-green-800 hover:bg-green-100" },
  raccrochage: { label: "Raccroché", className: "bg-amber-100 text-amber-800 hover:bg-amber-100" },
  silence: { label: "Sans réponse", className: "bg-gray-100 text-gray-500 hover:bg-gray-100" },
  max_tours: { label: "Limite atteinte", className: "bg-gray-100 text-gray-500 hover:bg-gray-100" },
  mauvaise_ligne: { label: "Ligne dégradée", className: "bg-red-100 text-red-800 hover:bg-red-100" },
};

const CallLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const loadLogs = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    const user = JSON.parse(localStorage.getItem("fruitycall_user") || "{}");
    if (!user.email) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/call-logs?user_email=${encodeURIComponent(user.email)}`
      );
      const data = await response.json();
      setLogs(data.call_logs || []);
    } catch (error) {
      console.error("Erreur chargement appels:", error);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
    const interval = setInterval(() => loadLogs(true), 60000);
    return () => clearInterval(interval);
  }, [loadLogs]);

  const formatDate = (dateStr) => {
    const date = parseApiDate(dateStr);
    if (!date || isNaN(date)) return "";
    return date.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (seconds == null) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins} min ${secs} s` : `${secs} s`;
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container-custom py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6" />
            Appels traités par l'IA
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Transcription complète de chaque conversation avec la secrétaire virtuelle. Utile pour
            vérifier la qualité des échanges et signaler ce qui doit être amélioré.
          </p>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Aucun appel enregistré pour le moment. Les conversations apparaîtront ici après chaque
              appel traité par la secrétaire virtuelle.
            </CardContent>
          </Card>
        ) : (
          logs.map((log) => {
            const term = TERMINAISON_LABELS[log.terminaison] || null;
            const isOpen = expanded === log._id;
            return (
              <Card key={log._id} className="mb-3">
                <CardContent className="p-4">
                  <button
                    className="w-full text-left"
                    onClick={() => setExpanded(isOpen ? null : log._id)}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold flex items-center gap-1">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {log.caller_phone || "Numéro inconnu"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(log.started_at || log.created_at)}
                        </span>
                        {log.duration_seconds != null && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDuration(log.duration_seconds)}
                          </span>
                        )}
                        {log.nb_tours != null && (
                          <span className="text-sm text-muted-foreground">
                            {log.nb_tours} échange{log.nb_tours > 1 ? "s" : ""}
                          </span>
                        )}
                        {term && <Badge className={term.className}>{term.label}</Badge>}
                      </div>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                    </div>
                    {!isOpen && log.resume?.motif && (
                      <p className="text-sm text-muted-foreground mt-1 truncate">
                        {log.resume.motif}
                      </p>
                    )}
                  </button>

                  {isOpen && (
                    <div className="mt-4 space-y-2 border-t pt-4">
                      {(log.transcript || []).map((turn, i) => (
                        <div
                          key={i}
                          className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                              turn.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-foreground"
                            }`}
                          >
                            <span className="flex items-center gap-1 text-xs opacity-70 mb-0.5">
                              {turn.role === "user" ? (
                                <><User className="h-3 w-3" /> Appelant</>
                              ) : (
                                <><Bot className="h-3 w-3" /> Secrétaire IA</>
                              )}
                            </span>
                            {turn.content}
                          </div>
                        </div>
                      ))}
                      {log.resume?.motif && (
                        <p className="text-xs text-muted-foreground pt-2">
                          Motif retenu : {log.resume.motif}
                          {log.resume.action ? ` — action : ${log.resume.action}` : ""}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </main>
    </div>
  );
};

export default CallLogs;
